/**
 * Activity feed — `tools/cache/activity.jsonl`, appended by the nightly
 * conductor (`tools/conductor.py`). One JSON object per line; never rewritten.
 */

export interface ActivityEntry {
  ts: string;
  actor: string;
  action: string;
  detail: string;
}

/** Parse JSONL, newest first; malformed lines are skipped, never fatal. */
export function parseActivity(text: string, limit = 80): ActivityEntry[] {
  const out: ActivityEntry[] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      const obj = JSON.parse(trimmed) as Partial<ActivityEntry>;
      out.push({
        ts: typeof obj.ts === "string" ? obj.ts : "",
        actor: typeof obj.actor === "string" ? obj.actor : "?",
        action: typeof obj.action === "string" ? obj.action : "",
        detail: typeof obj.detail === "string" ? obj.detail : "",
      });
    } catch {
      // A partially written last line must never break the feed.
    }
  }
  return out.reverse().slice(0, limit);
}

/** "vor 3 Min." style labels; falls back to the raw string when unparseable. */
export function relativeTime(ts: string, now: Date = new Date()): string {
  const t = new Date(ts);
  if (Number.isNaN(t.getTime())) return ts;
  const secs = Math.max(0, Math.round((now.getTime() - t.getTime()) / 1000));
  if (secs < 90) return "gerade eben";
  const mins = Math.round(secs / 60);
  if (mins < 90) return `vor ${mins} Min.`;
  const hours = Math.round(mins / 60);
  if (hours < 36) return `vor ${hours} Std.`;
  const days = Math.round(hours / 24);
  if (days < 14) return `vor ${days} Tg.`;
  return t.toISOString().slice(0, 10);
}

/** Short human label per actor for the feed icons. */
export function actorLabel(actor: string): string {
  const map: Record<string, string> = {
    nightly: "Nightly",
    conductor: "Conductor",
    consolidator: "Tidy-up",
    reviewer: "Reviewer",
    console: "You",
    planner: "Planner",
  };
  return map[actor] ?? actor;
}
